import camelcase from 'camelcase-keys';
import { v4 as uuidv4 } from 'uuid';

import { portalApi } from '../../../api';
import { parseLinkedElements } from '../../../pages/V2InsuredProfilePage/components/RecordDetailsContainer/components/RecordDetails/components/ActivityLog/util';
import colors from '../../../styles/theme/foundation/colors';
import { modifyArrayItemAt, removeArrayItemAt, removeArrayItemAtIndex } from '../../../utils/arrayUtil';
import { withSearchBlueprint } from '../../blueprints/search';
import { createErrorToast, createSuccessToast, createToast } from '../app';

/* Helpers
================================================== */
function createTempComment(user, message, linked, commentableId = null, tags = []) {
  return {
    id: uuidv4(),
    commentableId,
    comments: { data: [] },
    message,
    linked,
    tags,
    createdAt: (new Date()).toString(),
    updatedAt: (new Date()).toString(),
    activityType: 'comment',
    user: {
      id: user?.userId,
      firstName: user.userFirstName,
      lastName: user.userLastName
    }
  };
}

/* Actions
================================================== */
const ACTIVITY_LOG_GET_ACTIVITY_SUB_COMMENTS = 'ACTIVITY_LOG_GET_ACTIVITY_SUB_COMMENTS';
const ACTIVITY_LOG_GET_ACTIVITY_SUB_COMMENTS_SUCCESS = 'ACTIVITY_LOG_GET_ACTIVITY_SUB_COMMENTS_SUCCESS';
const ACTIVITY_LOG_GET_ACTIVITY_SUB_COMMENTS_FAILURE = 'ACTIVITY_LOG_GET_ACTIVITY_SUB_COMMENTS_FAILURE';
export const getActivitySubComments = (activityId, pageNumber) => async dispatch => {
  dispatch({ type: ACTIVITY_LOG_GET_ACTIVITY_SUB_COMMENTS, activityId });

  const pageData = { pageNumber, pageSize: 3 };

  try {
    const comments = camelcase(
      await portalApi.getActivitySubComments(activityId, pageData),
      { deep: true }
    );

    dispatch(activitySearch.updateItem(
      ({ activityType, id }) => activityType === 'activity' && parseInt(id, 10) === parseInt(activityId, 10),
      activity => ({
        ...activity,
        comments: {
          ...activity.comments,
          currentPage: activity.comments.currentPage + 1,
          data: [
            ...activity.comments.data,
            ...comments.data
          ]
        }
      })
    ));

    dispatch({ type: ACTIVITY_LOG_GET_ACTIVITY_SUB_COMMENTS_SUCCESS, comments, activityId });
  } catch (error) {
    dispatch({ type: ACTIVITY_LOG_GET_ACTIVITY_SUB_COMMENTS_FAILURE, error });
  }
};

export const getActivitySubCommentsBatch = activityIds => async dispatch => {
  if (!activityIds.length) return;

  dispatch({ type: ACTIVITY_LOG_GET_ACTIVITY_SUB_COMMENTS, activityIds });

  try {
    const commentBatch = camelcase(
      await portalApi.getActivitySubCommentsBatch(activityIds),
      { deep: true }
    );

    const updates = commentBatch.map(({ id: activityId, comments }) => ({
      identifier: ({
        activityType,
        id
      }) => activityType === 'activity' && parseInt(id, 10) === parseInt(activityId, 10),
      updateFn: activity => {
        if (!activity) return activity;

        return ({
          ...activity,
          comments: {
            ...activity.comments,
            data: [
              ...(activity.comments?.data || []),
              ...(comments?.data || [])
            ]
          }
        });
      }
    }));

    dispatch(activitySearch.updateItems(updates));

    dispatch({ type: ACTIVITY_LOG_GET_ACTIVITY_SUB_COMMENTS_SUCCESS, commentBatch });
  } catch (error) {
    dispatch({ type: ACTIVITY_LOG_GET_ACTIVITY_SUB_COMMENTS_FAILURE, error });
  }
};

const ACTIVITY_LOG_GET_COMMENT_SUB_COMMENTS = 'ACTIVITY_LOG_GET_COMMENT_SUB_COMMENTS';
const ACTIVITY_LOG_GET_COMMENT_SUB_COMMENTS_SUCCESS = 'ACTIVITY_LOG_GET_COMMENT_SUB_COMMENTS_SUCCESS';
const ACTIVITY_LOG_GET_COMMENT_SUB_COMMENTS_FAILURE = 'ACTIVITY_LOG_GET_COMMENT_SUB_COMMENTS_FAILURE';
export const getCommentSubCommentsBatch = commentIds => async dispatch => {
  if (!commentIds.length) return;

  dispatch({ type: ACTIVITY_LOG_GET_COMMENT_SUB_COMMENTS, commentIds });

  try {
    const commentBatch = camelcase(
      await portalApi.getCommentSubCommentsBatch(commentIds),
      { deep: true }
    );

    commentBatch.forEach(({ id: activityId, comments, linked }) => {
      dispatch(activitySearch.updateItem(
        ({ activityType, id }) => activityType === 'comment' && parseInt(id, 10) === parseInt(activityId, 10),
        activity => ({
          ...activity,
          linked: parseLinkedElements(linked),
          comments: {
            ...activity.comments,
            data: [
              ...(activity.comments?.data || []),
              ...comments.data
            ]
          }
        })
      ));
    });

    dispatch({ type: ACTIVITY_LOG_GET_COMMENT_SUB_COMMENTS_SUCCESS, commentBatch });
  } catch (error) {
    dispatch({ type: ACTIVITY_LOG_GET_COMMENT_SUB_COMMENTS_FAILURE, error });
  }
};

const ACTIVITY_LOG_OPEN_ADD_COMMENT = 'ACTIVITY_LOG_OPEN_ADD_COMMENT';
export const openAddComment = () => dispatch => {
  dispatch({ type: ACTIVITY_LOG_OPEN_ADD_COMMENT });
};

const ACTIVITY_LOG_CLOSE_ADD_COMMENT = 'ACTIVITY_LOG_CLOSE_ADD_COMMENT';
export const closeAddComment = () => dispatch => {
  dispatch({ type: ACTIVITY_LOG_CLOSE_ADD_COMMENT });
};

const ACTIVITY_LOG_OPEN_SUB_COMMENT = 'ACTIVITY_LOG_OPEN_SUB_COMMENT';
export const openSubComment = id => dispatch => {
  dispatch({ type: ACTIVITY_LOG_OPEN_SUB_COMMENT, id });
};

const ACTIVITY_LOG_CLOSE_SUB_COMMENT = 'ACTIVITY_LOG_CLOSE_SUB_COMMENT';
export const closeSubComment = () => dispatch => {
  dispatch({ type: ACTIVITY_LOG_CLOSE_SUB_COMMENT });
};

const ACTIVITY_LOG_SET_TYPE_FILTER = 'ACTIVITY_LOG_SET_TYPE_FILTER';
export const setFilterType = (filter, insuredId = null, recordId = null) => async dispatch => {
  dispatch({ type: ACTIVITY_LOG_SET_TYPE_FILTER, filter });

  if (filter === 'record_activity') {
    dispatch(activitySearch.setBaseFilters([
      { key: 'insurance_requirement.id', value: recordId }
    ]));
  } else if (filter === 'all_activity') {
    dispatch(activitySearch.setBaseFilters([
      { key: 'insured.id', value: insuredId }
    ]));
  } else {
    dispatch(activitySearch.setBaseFilters([
      { key: 'activity_type', value: filter },
      { key: 'insured.id', value: insuredId }
    ]));
  }
};

const ACTIVITY_LOG_CREATE_COMMENT = 'ACTIVITY_LOG_CREATE_COMMENT';
const ACTIVITY_LOG_CREATE_COMMENT_SUCCESS = 'ACTIVITY_LOG_CREATE_COMMENT_SUCCESS';
const ACTIVITY_LOG_CREATE_COMMENT_FAILURE = 'ACTIVITY_LOG_CREATE_COMMENT_FAILURE';
export const createCommentForInsured = (insuredId, html) => async (dispatch, getState) => {
  const linkToComment = selectElementsToLinkToComment(getState());
  const { user } = getState();

  const linked = parseLinkedElements(linkToComment);

  const tempComment = createTempComment(
    user,
    html,
    linked
  );

  dispatch({ type: ACTIVITY_LOG_CREATE_COMMENT, tempComment });

  dispatch(activitySearch.prependItem(tempComment));

  try {
    const response = camelcase(
      await portalApi.createInsuredActivityComment(insuredId, { message: html }),
      { deep: true }
    );

    await Promise.all(linkToComment.map(entity => portalApi.createCommentLink(
      response.id,
      entity.id,
      {
        entityType: entity.type,
        description: entity.description
      }
    )));

    dispatch(activitySearch.updateItem('id', tempComment.id, comment => (
      {
        ...comment,
        id: response.id,
        activityType: 'comment'
      }
    )));
  } catch (error) {
    dispatch(activitySearch.removeItem('id', tempComment.id));
    dispatch({ type: ACTIVITY_LOG_CREATE_COMMENT_FAILURE, error });
  }
};

export const createCommentForRecord = (recordId, html) => async (dispatch, getState) => {
  const linkToComment = selectElementsToLinkToComment(getState());
  const { user } = getState();

  const linked = parseLinkedElements(linkToComment);
  const taggedUsers = selectTagUsersToComment(getState());
  const taggedUserIds = taggedUsers.map(({ id }) => id);

  const tempComment = createTempComment(
    user,
    html,
    linked,
    null,
    taggedUsers
  );

  dispatch({ type: ACTIVITY_LOG_CREATE_COMMENT, tempComment });

  dispatch(activitySearch.prependItem(tempComment));

  try {
    const response = camelcase(
      await portalApi.createRecordComment(recordId, { message: html, user_ids: taggedUserIds }),
      { deep: true }
    );

    await Promise.all(linkToComment.map(entity => portalApi.createCommentLink(
      response.id,
      entity.id,
      {
        entityType: entity.type,
        description: entity.description
      }
    )));

    dispatch(activitySearch.updateItem('id', tempComment.id, comment => (
      {
        ...comment,
        id: response.id,
        activityType: 'comment'
      }
    )));

    dispatch({ type: ACTIVITY_LOG_CREATE_COMMENT_SUCCESS, comment: response });
  } catch (error) {
    dispatch(activitySearch.removeItem('id', tempComment.id));
    dispatch({ type: ACTIVITY_LOG_CREATE_COMMENT_FAILURE, error });
  }
};

const ACTIVITY_LOG_CREATE_ACTIVITY_SUB_COMMENT = 'ACTIVITY_LOG_CREATE_ACTIVITY_SUB_COMMENT';
const ACTIVITY_LOG_CREATE_ACTIVITY_SUB_COMMENT_SUCCESS = 'ACTIVITY_LOG_CREATE_ACTIVITY_SUB_COMMENT_SUCCESS';
const ACTIVITY_LOG_CREATE_ACTIVITY_SUB_COMMENT_FAILURE = 'ACTIVITY_LOG_CREATE_ACTIVITY_SUB_COMMENT_FAILURE';
export const createActivitySubComment = (html, activityId) => async (dispatch, getState) => {
  const { user } = getState();
  const taggedUsers = selectTagUsersToComment(getState());
  const taggedUserIds = taggedUsers.map(({ id }) => id);

  const tempComment = createTempComment(
    user,
    html,
    null,
    activityId,
    taggedUsers
  );

  dispatch({ type: ACTIVITY_LOG_CREATE_ACTIVITY_SUB_COMMENT });
  dispatch(activitySearch.updateItem(
    ({ activityType, id }) => activityType === 'activity' && id === activityId,
    activity => ({
      ...activity,
      comments: {
        data: [
          tempComment, ...(activity.comments?.data || [])
        ]
      }
    })
  ));

  try {
    const comment = camelcase(
      await portalApi.createActivitySubComment(activityId, { message: html, user_ids: taggedUserIds }),
      { deep: true }
    );

    dispatch({ type: ACTIVITY_LOG_CREATE_ACTIVITY_SUB_COMMENT_SUCCESS });
    dispatch(activitySearch.updateItem(
      ({ activityType, id }) => activityType === 'activity' && id === activityId,
      activity => ({
        ...activity,
        comments: {
          ...activity.comments,
          data: modifyArrayItemAt(
            activity.comments.data,
            'id',
            tempComment.id,
            tmp => ({
              ...tmp,
              ...comment
            })
          )
        }
      })
    ));
  } catch (error) {
    dispatch({ type: ACTIVITY_LOG_CREATE_ACTIVITY_SUB_COMMENT_FAILURE, error });
  }
};

const ACTIVITY_LOG_CREATE_COMMENT_SUB_COMMENT = 'ACTIVITY_LOG_CREATE_COMMENT_SUB_COMMENT';
const ACTIVITY_LOG_CREATE_COMMENT_SUB_COMMENT_SUCCESS = 'ACTIVITY_LOG_CREATE_COMMENT_SUB_COMMENT_SUCCESS';
const ACTIVITY_LOG_CREATE_COMMENT_SUB_COMMENT_FAILURE = 'ACTIVITY_LOG_CREATE_COMMENT_SUB_COMMENT_FAILURE';
export const createCommentSubComment = (html, commentId) => async (dispatch, getState) => {
  const { user } = getState();
  const taggedUsers = selectTagUsersToComment(getState());
  const taggedUserIds = taggedUsers.map(({ id }) => id);

  dispatch({ type: ACTIVITY_LOG_CREATE_COMMENT_SUB_COMMENT, html, taggedUsers });

  const tempComment = createTempComment(
    user,
    html,
    null,
    commentId,
    taggedUsers
  );

  dispatch(activitySearch.updateItem(
    ({ activityType, id }) => activityType === 'comment' && id === commentId,
    comment => ({
      ...comment,
      comments: {
        data: [
          tempComment, ...(comment.comments?.data || [])
        ]
      }
    })
  ));

  try {
    const comment = camelcase(
      await portalApi.createCommentSubComment(commentId, { message: html, user_ids: taggedUserIds }),
      { deep: true }
    );

    dispatch(activitySearch.updateItem(
      ({ activityType, id }) => activityType === 'comment' && id === commentId,
      parentComment => ({
        ...parentComment,
        comments: {
          ...parentComment.comments,
          data: modifyArrayItemAt(
            parentComment.comments.data,
            'id',
            tempComment.id,
            tmp => ({
              ...tmp,
              ...comment
            })
          )
        }
      })
    ));

    dispatch({ type: ACTIVITY_LOG_CREATE_COMMENT_SUB_COMMENT_SUCCESS });
  } catch (error) {
    dispatch({ type: ACTIVITY_LOG_CREATE_COMMENT_SUB_COMMENT_FAILURE, error });
    dispatch(createErrorToast({
      title: 'Something went wrong.',
      description: 'Your comment wasn\'t created. Please refresh your browser and try again.',
      error
    }));
  }
};

const ACTIVITY_LOG_UPDATE_COMMENT = 'ACTIVITY_LOG_UPDATE_COMMENT';
const ACTIVITY_LOG_UPDATE_COMMENT_SUCCESS = 'ACTIVITY_LOG_UPDATE_COMMENT_SUCCESS';
const ACTIVITY_LOG_UPDATE_COMMENT_FAILURE = 'ACTIVITY_LOG_UPDATE_COMMENT_FAILURE';

export const updateComment = (commentId, html) => async (dispatch, getState) => {
  const { user } = getState();

  dispatch({ type: ACTIVITY_LOG_UPDATE_COMMENT });

  try {
    dispatch(activitySearch.updateItem(
      ({ activityType, id }) => activityType === 'comment' && id === commentId,
      parentComment => ({
        ...parentComment,
        message: html
      })
    ));

    dispatch(createSuccessToast({ title: 'Comment Updated!' }));

    const comment = camelcase(
      await portalApi.updateComment(commentId, { message: html }),
      { deep: true }
    );

    dispatch({ type: ACTIVITY_LOG_UPDATE_COMMENT_SUCCESS, comment });
  } catch (error) {
    dispatch(createErrorToast({
      title: 'Something went wrong.',
      description: 'Your comment wasn\'t updated.',
      error
    }));

    await dispatch(activitySearch.getPage());

    dispatch({ type: ACTIVITY_LOG_UPDATE_COMMENT_FAILURE, error });
  }
};

const ACTIVITY_LOG_DELETE_COMMENT = 'ACTIVITY_LOG_DELETE_COMMENT';
const ACTIVITY_LOG_DELETE_COMMENT_SUCCESS = 'ACTIVITY_LOG_DELETE_COMMENT_SUCCESS';
const ACTIVITY_LOG_DELETE_COMMENT_FAILURE = 'ACTIVITY_LOG_DELETE_COMMENT_FAILURE';

export const deleteComment = commentId => async (dispatch, getState) => {
  const { user } = getState();

  dispatch({ type: ACTIVITY_LOG_DELETE_COMMENT, commentId });

  dispatch(createSuccessToast({ title: 'Comment Deleted!' }));

  try {
    dispatch(activitySearch.removeItem('id', commentId));

    const comment = camelcase(
      await portalApi.deleteComment(commentId),
      { deep: true }
    );

    dispatch({ type: ACTIVITY_LOG_DELETE_COMMENT_SUCCESS, comment });
  } catch (error) {
    dispatch(createErrorToast({
      title: 'Something went wrong.',
      description: 'Your comment wasn\'t deleted.',
      error
    }));

    await dispatch(activitySearch.getPage());

    dispatch({ type: ACTIVITY_LOG_DELETE_COMMENT_FAILURE, error });
  }
};

const ACTIVITY_LOG_DELETE_COMMENT_SUB_COMMENT = 'ACTIVITY_LOG_DELETE_COMMENT_SUB_COMMENT';
const ACTIVITY_LOG_DELETE_COMMENT_SUB_COMMENT_SUCCESS = 'ACTIVITY_LOG_DELETE_COMMENT_SUB_COMMENT_SUCCESS';
const ACTIVITY_LOG_DELETE_COMMENT_SUB_COMMENT_FAILURE = 'ACTIVITY_LOG_DELETE_COMMENT_SUB_COMMENT_FAILURE';

export const deleteCommentSubComment = (parentId, commentId) => async (dispatch, getState) => {
  dispatch({ type: ACTIVITY_LOG_DELETE_COMMENT_SUB_COMMENT, parentCommentId: parentId, commentId });

  dispatch(createSuccessToast({ title: 'Comment Deleted!' }));

  try {
    dispatch(activitySearch.updateItem(
      ({ activityType, id }) => activityType === 'comment' && id === parentId,
      parentComment => ({
        ...parentComment,
        comments: {
          ...parentComment.comments,
          data: removeArrayItemAt(
            parentComment.comments.data,
            'id',
            commentId
          )
        }
      })
    ));

    const comment = camelcase(
      await portalApi.deleteComment(commentId),
      { deep: true }
    );

    dispatch({ type: ACTIVITY_LOG_DELETE_COMMENT_SUB_COMMENT_SUCCESS, comment });
  } catch (error) {
    dispatch(createErrorToast({
      title: 'Something went wrong.',
      description: 'Your comment wasn\'t deleted.',
      error
    }));

    await dispatch(activitySearch.getPage());

    dispatch({ type: ACTIVITY_LOG_DELETE_COMMENT_SUB_COMMENT_FAILURE, error });
  }
};

const ACTIVITY_LOG_UPDATE_COMMENT_SUB_COMMENT = 'ACTIVITY_LOG_UPDATE_COMMENT_SUB_COMMENT';
const ACTIVITY_LOG_UPDATE_COMMENT_SUB_COMMENT_SUCCESS = 'ACTIVITY_LOG_UPDATE_COMMENT_SUB_COMMENT_SUCCESS';
const ACTIVITY_LOG_UPDATE_COMMENT_SUB_COMMENT_FAILURE = 'ACTIVITY_LOG_UPDATE_COMMENT_SUB_COMMENT_FAILURE';

export const updateCommentSubComment = (parentId, parentType, commentId, html) => async (dispatch, getState) => {
  dispatch({ type: ACTIVITY_LOG_UPDATE_COMMENT_SUB_COMMENT, parentId, parentType, commentId, html });

  let matchType = 'activity';

  if (parentType === 'activity') {
    matchType = 'activity';
  } else if (parentType === 'comment') {
    matchType = 'comment';
  }

  try {
    dispatch(activitySearch.updateItem(
      ({ activityType, id }) => activityType === matchType && id === parentId,
      parentComment => ({
        ...parentComment,
        comments: {
          ...parentComment.comments,
          data: modifyArrayItemAt(
            parentComment.comments.data,
            'id',
            commentId,
            comment => ({
              ...comment,
              message: html
            })
          )
        }
      })
    ));

    dispatch(createSuccessToast({ title: 'Comment Updated!' }));

    const comment = camelcase(
      await portalApi.updateComment(commentId, { message: html }),
      { deep: true }
    );

    dispatch({ type: ACTIVITY_LOG_UPDATE_COMMENT_SUB_COMMENT_SUCCESS, comment });
  } catch (error) {
    dispatch(createErrorToast({
      title: 'Something went wrong.',
      description: 'Your comment wasn\'t updated.',
      error
    }));

    await dispatch(activitySearch.getPage());

    dispatch({ type: ACTIVITY_LOG_UPDATE_COMMENT_SUB_COMMENT_FAILURE, error });
  }
};

const ACTIVITY_LOG_ADD_LINKED_ENTITIES = 'ACTIVITY_LOG_ADD_LINKED_ENTITIES';
export const addLinkedEntity = checkedEntity => dispatch => {
  dispatch({ type: ACTIVITY_LOG_ADD_LINKED_ENTITIES, checkedEntity });
};

const ACTIVITY_LOG_REMOVE_LINKED_ENTITIES = 'ACTIVITY_LOG_REMOVE_LINKED_ENTITIES';
export const removeLinkedEntity = checkedEntity => dispatch => {
  dispatch({ type: ACTIVITY_LOG_REMOVE_LINKED_ENTITIES, checkedEntity });
};

const ACTIVITY_LOG_CHECK_ALL_LINKED_ENTITIES = 'ACTIVITY_LOG_CHECK_ALL_LINKED_ENTITIES';
export const checkAllLinkedEntities = (updatedEntities, entityType) => dispatch => {
  dispatch({ type: ACTIVITY_LOG_CHECK_ALL_LINKED_ENTITIES, updatedEntities, entityType });
};

const ACTIVITY_LOG_UNLINK_ALL_ELEMENTS_TO_COMMENT = 'ACTIVITY_LOG_UNLINK_ALL_ELEMENTS_TO_COMMENT';
export const unlinkAllEntitiesToComment = () => dispatch => {
  dispatch({ type: ACTIVITY_LOG_UNLINK_ALL_ELEMENTS_TO_COMMENT });
};

const ACTIVITY_LOG_SET_USER_ICON_COLOR = 'ACTIVITY_LOG_SET_USER_ICON_COLOR';
export const setUserIconColor = userInitials => (dispatch, getState) => {
  const users = selectActivityLogUsers(getState());
  const avatarColors = Object.values(colors.avatarColors);

  const existingUser = users.find(user => user.userInitials === userInitials);

  if (!existingUser) {
    const appliedColor = avatarColors[(users.length % avatarColors.length)];
    dispatch({ type: ACTIVITY_LOG_SET_USER_ICON_COLOR, newUser: { userInitials, appliedColor } });
  }
};

const ACTIVITY_LOG_UPDATE_LINKED_ENTITIES = 'ACTIVITY_LOG_UPDATE_LINKED_ENTITIES';
export const updateLinkedEntities = (selected, entityType) => dispatch => {
  dispatch({ type: ACTIVITY_LOG_UPDATE_LINKED_ENTITIES, selected, entityType });
};

const ACTIVITY_LOG_TAG_USER_TO_COMMENT = 'ACTIVITY_LOG_TAG_USER_TO_COMMENT';

export const tagUserToComment = selected => dispatch => {
  dispatch({ type: ACTIVITY_LOG_TAG_USER_TO_COMMENT, selected });
};

const ACTIVITY_LOG_RESET_TAGGED_USERS_TO_COMMENT = 'ACTIVITY_LOG_RESET_TAGGED_USERS_TO_COMMENT';

export const resetTaggedUsersToComment = () => dispatch => {
  dispatch({ type: ACTIVITY_LOG_RESET_TAGGED_USERS_TO_COMMENT });
};

const ACTIVITY_LOG_RESET = 'ACTIVITY_LOG_RESET';
export const activityLogReset = () => dispatch => {
  dispatch({ type: ACTIVITY_LOG_RESET });
};

/* Reducer
================================================== */
// TODO: rm hardcoded data for docs and policies
const DEFAULT_STATE = {
  addingComment: false,
  addingSubComment: null,
  activities: [],
  pageInfo: {},
  loading: false,
  linkToComment: [],
  tagUsersToComment: [],
  filters: {
    units: [],
    codes: [],
    codeActions: [],
    userName: [],
    dateFrom: null,
    dateTo: null
  },
  typeFilter: 'all_activity',
  users: []
};

const activityLogReducer = (state = DEFAULT_STATE, action) => {
  switch (action.type) {
    case ACTIVITY_LOG_GET_ACTIVITY_SUB_COMMENTS:
      return { ...state, fetchingActivitySubComments: true };

    case ACTIVITY_LOG_GET_ACTIVITY_SUB_COMMENTS_SUCCESS:
      return { ...state, fetchingActivitySubComments: false };

    case ACTIVITY_LOG_GET_COMMENT_SUB_COMMENTS:
      return { ...state, fetchingCommentSubComments: true };

    case ACTIVITY_LOG_GET_COMMENT_SUB_COMMENTS_SUCCESS:
      return { ...state, fetchingCommentSubComments: false };

    case ACTIVITY_LOG_GET_ACTIVITY_SUB_COMMENTS_FAILURE:
      return { ...state, error: action.error };

    case ACTIVITY_LOG_OPEN_ADD_COMMENT:
      return { ...state, addingComment: true };

    case ACTIVITY_LOG_CLOSE_ADD_COMMENT:
      return { ...state, addingComment: false };

    case ACTIVITY_LOG_OPEN_SUB_COMMENT:
      return { ...state, addingSubComment: action.id };

    case ACTIVITY_LOG_CLOSE_SUB_COMMENT:
      return { ...state, addingSubComment: null, tagUsersToComment: [] };

    case ACTIVITY_LOG_SET_TYPE_FILTER:
      return { ...state, typeFilter: action.filter };

    case ACTIVITY_LOG_CREATE_COMMENT_SUCCESS: {
      return { ...state, linkToComment: [], tagUsersToComment: [] };
    }

    case ACTIVITY_LOG_CREATE_ACTIVITY_SUB_COMMENT_SUCCESS:
      return { ...state, tagUsersToComment: [] };

    case ACTIVITY_LOG_CREATE_COMMENT_SUB_COMMENT_SUCCESS:
      return { ...state, tagUsersToComment: [] };

    case ACTIVITY_LOG_CREATE_COMMENT_FAILURE:
      return { ...state, activities: action.error, linkToComment: [] };

    case ACTIVITY_LOG_CREATE_ACTIVITY_SUB_COMMENT_FAILURE:
      return { ...state, activities: [], error: action.error };

    case ACTIVITY_LOG_CREATE_COMMENT_SUB_COMMENT_FAILURE:
      return { ...state, activities: [], error: action.error };

    case ACTIVITY_LOG_ADD_LINKED_ENTITIES:
      return { ...state, linkToComment: [...state.linkToComment, action.checkedEntity] };

    case ACTIVITY_LOG_REMOVE_LINKED_ENTITIES: {
      const updatedList = state.linkToComment.filter(entity => entity.id !== action.checkedEntity.id);
      return { ...state, linkToComment: updatedList };
    }

    case ACTIVITY_LOG_CHECK_ALL_LINKED_ENTITIES: {
      const unchangedEntities = state.linkToComment.filter(e => e.type !== action.entityType);
      return { ...state, linkToComment: [...unchangedEntities, ...action.updatedEntities] };
    }

    case ACTIVITY_LOG_UNLINK_ALL_ELEMENTS_TO_COMMENT:
      return { ...state, linkToComment: [] };

    case ACTIVITY_LOG_UPDATE_LINKED_ENTITIES: {
      const { entityType } = action;
      let updatedLinked;
      const unchangedEntities = state.linkToComment.filter(item => item.type !== entityType);

      if (action.selected === undefined || !action.selected.length) {
        updatedLinked = [];
      }

      updatedLinked = action.selected.map(item => {
        const index = state.linkToComment.indexOf(item);

        if (index >= 0 && item.type === entityType) removeArrayItemAtIndex(state.linkToComment, index);

        if (index < 0) return item;
      });

      return { ...state, linkToComment: [...unchangedEntities, ...updatedLinked] };
    }

    case ACTIVITY_LOG_TAG_USER_TO_COMMENT:
      return { ...state, tagUsersToComment: action.selected };

    case ACTIVITY_LOG_RESET_TAGGED_USERS_TO_COMMENT:
      return { ...state, tagUsersToComment: [] };

    case ACTIVITY_LOG_SET_USER_ICON_COLOR:
      return { ...state, users: [...state.users, action.newUser] };

    case ACTIVITY_LOG_RESET:
      return DEFAULT_STATE;

    default:
      return state;
  }
};

/* Selectors
================================================== */
export const selectAddingComment = state => state.activityLog.base.addingComment;
export const selectIsFetchingActivitySubComments = state => state.activityLog.base.fetchingActivitySubComments;
export const selectIsFetchingCommentSubComments = state => state.activityLog.base.fetchingCommentSubComments;
export const selectSubComment = state => state.activityLog.base.addingSubComment;
export const selectTypeFilter = state => state.activityLog.base.typeFilter;
export const selectElementsToLinkToComment = state => state.activityLog.base.linkToComment;
export const selectCurrentUserIconColor = userInitials => state => state.activityLog.base.users.filter(user => user.userInitials === userInitials)[0];
export const selectActivityLogUsers = state => state.activityLog.base.users;
export const selectTagUsersToComment = state => state.activityLog.base.tagUsersToComment;

export const { reducer, ...activitySearch } = withSearchBlueprint('activityLog', activityLogReducer, { endpoint: portalApi.searchInsuredActivity });
